import { Navigate, useParams } from 'react-router-dom';

/**
 * Retired routes (authorization part 7g): each leads to the page that replaced it, or to
 * /nopermission when there is none. The target pages carry their own gate. The old page
 * components stay in the code, unreachable.
 */
export const RETIRED_ROUTES = {
  /** /propertyprofile/:id(/:flag) → partner profile. */
  propertyProfile: (id: number) => `/partnerProfile/${id}`,
  /** /editandviewactivation/:id → activity view. */
  editAndViewActivation: (id: number) => `/viewActivity/${id}`,
  /** /viewcampaign/:id → campaign (activation) view. */
  viewCampaign: (id: number) => `/viewActivation/${id}`,
  /** /storecampaign → create campaign (activation). */
  storeCampaign: () => '/addActivation',
  /** /storecampaign/:id (part 7j) → campaign (activation) view. */
  storeCampaignId: (id: number) => `/viewActivation/${id}`,
  /**
   * /activationform and /activationform/:idtype/:id (part 7j): the old create-activity form
   * (idtype brand / property only preselected the owner) → the current create form, which takes
   * the brand / partner from the header.
   */
  activationForm: () => '/addActivity',
  /** /revenueoutput/:id → contract view (its ROI tab holds the revenue output). */
  revenueOutput: (id: number) => `/viewcontract/${id}`,
  /** /brand-overview/:id was a static mock without a successor. */
  brandOverview: () => null,
} as const satisfies Record<string, (id: number) => string | null>;

export type RetiredRoute = keyof typeof RETIRED_ROUTES;

/** Where a retired route leads; /nopermission without a successor or a valid id. */
export function retiredTarget(route: RetiredRoute, id?: string): string {
  const needsId = RETIRED_ROUTES[route].length > 0;
  const numericId = id !== undefined && /^[1-9]\d*$/.test(id) ? Number(id) : null;
  if (needsId && numericId === null) return '/nopermission';
  return RETIRED_ROUTES[route](numericId ?? 0) ?? '/nopermission';
}

export default function RetiredRedirect({ route }: { route: RetiredRoute }) {
  const { id } = useParams();
  return <Navigate replace to={retiredTarget(route, id)} />;
}
